import { Chakra } from "@airhelp/react-ui-components";
import BreadcrumbsSchema from "modules/storyblok/components/BreadcrumbsSchema";
import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import config from "config/config";
import { DataContextProvider } from "contexts/DataContext";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { MobileMenuContextProvider } from "contexts/MobileMenuContext";
import { ModalContextProvider } from "contexts/ModalContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import CustomHead from "modules/common/components/CustomHead";
import Footer from "modules/common/components/Footer";
import HeaderNav from "modules/common/components/HeaderNav";
import CheckCompensationButton from "modules/common/components/HeaderNav/CheckCompensationButton";
import MenuItems from "modules/common/components/HeaderNav/MenuItems";
import PostListItem from "modules/common/components/PostListItem";
import PressLogos from "modules/common/components/PressLogos";
import PressAssetItem from "modules/press/components/PressAssetItem";
import PressCoverageItem from "modules/press/components/PressCoverageItem";
import PropTypes from "prop-types";
import { useState } from "react";
import getPageUrl from "utils/getPageUrl";
import { useTopMenu } from "utils/menuUtils";
import StickyBottomBarAdapter from "../adapters/StickyBottomBar";

const { Box, Button, Flex, Grid, GridItem, Heading, Text } = Chakra;

const PressHomeTemplate = ({ story, stories, cmsLanguagesData, total, whiteListDomains, serverSideStoryblokPages }) => {
  const language = useLanguage();
  const translations = useTranslations();
  const topMenu = useTopMenu(language);
  const [loadedStories, setLoadedStories] = useState([]);
  const [storiesLeft, setStoriesLeft] = useState(total - 3);
  const [storiesPage, setStoriesPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const {
    content: {
      metaDescription,
      metaTitle,
      breadcrumbsParentPage,
      breadcrumbsPositionName,
      stickyBottomBarHeading,
      hideStickyBottomBar,
      stickyBottomBarVariant,
    },
  } = story;

  const loadMorePosts = async () => {
    setIsLoading(true);
    const nextPage = storiesPage + 1;
    const response = await fetch(`/api/press-posts?language=${language}&page=${nextPage}`);
    const { stories: responseStories } = await response.json();

    setLoadedStories([...loadedStories, ...responseStories]);

    setStoriesPage(nextPage);
    setStoriesLeft(storiesLeft - 3);
    setIsLoading(false);
  };

  const translationVariables = {
    "%help-link-attrs%": `href="${getPageUrl(language, "help")}"`,
    "%mail-link-attrs%": "href='mailto:press@airhelp.com'",
  };

  const contactSectionText = translations("only_media_inquiries", translationVariables);

  return (
    <DataContextProvider data={{ cmsLanguagesData, serverSideStoryblokPages, whiteListDomains }}>
      <CustomHead title={`${metaTitle} - AirHelp`} description={metaDescription} />
      <BreadcrumbsSchema
        breadcrumbsPositionName={breadcrumbsPositionName}
        breadcrumbsParentPage={breadcrumbsParentPage}
      />
      <MobileMenuContextProvider>
        <ModalContextProvider>
          <HeaderNav menuItems={<MenuItems menu={topMenu} />} headerButtons={<CheckCompensationButton />} />
          <Box mt={32} width="100%" maxWidth="1202px" px={4} mx="auto" as="main">
            <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
            <Grid mb={12} templateColumns={{ base: "1fr", md: "10fr 2fr" }}>
              <GridItem mb={2} colSpan={{ base: 1, md: 2 }}>
                <Heading>{translations("airhelp_news")}</Heading>
              </GridItem>
              <GridItem colSpan={1}>
                <Text>{translations("airhelp_is_the_leading")}</Text>
              </GridItem>
            </Grid>

            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} columnGap={8}>
              {stories.map((pressStory) => (
                <PostListItem
                  key={pressStory.uuid}
                  story={pressStory}
                  showExcerpt={false}
                  showReadMore={false}
                  showAuthor={false}
                  showDate
                />
              ))}
              {loadedStories &&
                loadedStories.map((pressStory) => (
                  <PostListItem
                    key={pressStory.uuid}
                    story={pressStory}
                    showExcerpt={false}
                    showReadMore={false}
                    showAuthor={false}
                    showDate
                  />
                ))}
            </Grid>
            <Flex display={storiesLeft > 0 ? "flex" : "none"} justifyContent="center" mb={{ base: 14, md: 24 }}>
              <Button
                variant="secondary"
                size="l"
                width={{ base: "100%", md: "initial" }}
                isLoading={isLoading}
                onClick={loadMorePosts}
              >
                {translations("see_more")}
              </Button>
            </Flex>
          </Box>

          <Box
            bgColor="greyscale.300"
            bgRepeat="no-repeat"
            bgImage={{
              base: "none",
              lg: `url(${config.staticUrl}/i/airhelp-plus/ahplus-landing-page-bg-fragment-triangles-01.svg)`,
            }}
            py={{ base: 14, lg: 28 }}
            bgPosition="left top 48px"
          >
            <Box width="100%" maxWidth="1202px" px={4} mx="auto">
              <Heading mb={12}>{translations("press_assets")}</Heading>
              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} columnGap={8}>
                <GridItem mb={{ base: 12, lg: 0 }}>
                  <PressAssetItem href="https://static.airhelp.com/Logotypes.zip" title={translations("logos")} />
                </GridItem>
                <GridItem mb={{ base: 12, lg: 0 }}>
                  <PressAssetItem
                    href="https://static.airhelp.com/Product_shots.zip"
                    image={`${config.staticUrl}/i/press/product-images.png`}
                    title={translations("product_images")}
                  />
                </GridItem>
                <GridItem>
                  <PressAssetItem
                    href="https://static.airhelp.com/Founders.zip"
                    image={`${config.staticUrl}/i/press/photos.png`}
                    title={translations("photos")}
                  />
                </GridItem>
              </Grid>
            </Box>
          </Box>

          <Box my={{ base: 14, lg: 24 }} width="100%" maxWidth="1202px" px={4} mx="auto">
            <Heading mb={12}>{translations("press_coverage")}</Heading>
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} columnGap={8}>
              <GridItem mb={{ base: 12, lg: 0 }}>
                <PressCoverageItem
                  href="https://www.forbes.com/sites/alexandratalty/2020/02/21/new-report-finds-us-airlines-are-wrongfully-rejecting-25-of-passenger-claims/"
                  image={`${config.staticUrl}/i/press/forbes.png`}
                  title="New Report Finds US Airlines Are Wrongfully Rejecting 25% Of Passenger Claims"
                />
              </GridItem>
              <GridItem mb={{ base: 12, lg: 0 }}>
                <PressCoverageItem
                  href="https://medium.com/@traveld/ryanair-compensation-a22d0c83616e"
                  image={`${config.staticUrl}/i/press/medium.png`}
                  title="AirHelp won a case against Ryanair"
                />
              </GridItem>
              <GridItem>
                <PressCoverageItem
                  href="https://www.telegraph.co.uk/travel/news/consumer-trust-in-airlines-at-alarming-low-study/"
                  image={`${config.staticUrl}/i/press/the-telegraph.png`}
                  title="Consumer trust in airlines at alarming low, says new study"
                />
              </GridItem>
            </Grid>
          </Box>

          <Box mb={{ base: 14, lg: 24 }} bgColor={{ base: "primary.900", lg: "transparent" }}>
            <Box width="100%" maxWidth="1202px" px={4} mx="auto">
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 1fr" }}
                rounded="xl"
                bgColor="primary.900"
                bgImage={{
                  base: "none",
                  lg: `url(${config.staticUrl}/i/airhelp-plus/ahplus-landing-page-bg-fragment-triangles-02.svg)`,
                }}
                bgRepeat="no-repeat"
                bgPosition="right center"
                color="white"
                px={{ base: 0, lg: 8 }}
                py={{ base: 14, lg: 8 }}
              >
                <GridItem mb={2} colSpan={{ base: 1, md: 2 }}>
                  <Heading color="inherit" size="md" mb={4}>
                    {translations("contact")}
                  </Heading>
                </GridItem>
                <GridItem colSpan={1}>
                  <Text
                    color="inherit"
                    sx={{ a: { color: "primary.500", _hover: { color: "primary.400" } } }}
                    dangerouslySetInnerHTML={{ __html: contactSectionText }}
                  />
                </GridItem>
              </Grid>
            </Box>
          </Box>

          <PressLogos />
          <Footer />
        </ModalContextProvider>
      </MobileMenuContextProvider>
      <StickyBottomBarAdapter
        showStickyBottomBar={!hideStickyBottomBar}
        stickyBottomBarVariant={stickyBottomBarVariant}
        stickyBottomBarHeading={stickyBottomBarHeading}
      />
    </DataContextProvider>
  );
};

const PressHomeTemplateAdapter = (props) => (
  <StickyBottomBarContextProvider threshold={0.4}>
    <PressHomeTemplate {...props} />
  </StickyBottomBarContextProvider>
);

export default PressHomeTemplateAdapter;

PressHomeTemplate.propTypes = {
  story: PropTypes.shape({
    content: PropTypes.shape({
      metaTitle: PropTypes.string.isRequired,
      metaDescription: PropTypes.string.isRequired,
      breadcrumbsParentPage: PropTypes.shape({}),
      breadcrumbsPositionName: PropTypes.string,
    }),
  }).isRequired,
  stories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      content: PropTypes.shape({
        body: PropTypes.arrayOf(
          PropTypes.shape({
            component: PropTypes.string,
            content: PropTypes.shape({
              content: PropTypes.string,
            }),
          })
        ),
      }),
    })
  ).isRequired,
  cmsLanguagesData: PropTypes.arrayOf(
    PropTypes.shape({
      full_slug: PropTypes.string,
    })
  ),
  total: PropTypes.number.isRequired,
  whiteListDomains: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      dimension_value: PropTypes.string,
    })
  ),
  serverSideStoryblokPages: PropTypes.shape({}).isRequired,
};

PressHomeTemplate.defaultProps = {
  cmsLanguagesData: [],
  whiteListDomains: [],
};
